import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import api from '../../utils/axiosConfig'; // Use configured API instance
import AuthContext from './AuthContext'; // Make sure this path is correct
import { canGoBack, getPreviousPath } from '../../utils/navHistory';
import './Login.css';

const NativeGoogleAuth = registerPlugin('NativeGoogleAuth');

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appleIdInitialized, setAppleIdInitialized] = useState(false);

  // Use the AuthContext for login/registration
  const { login, register: registerUser, appleLogin, googleLogin, isAuthenticated } = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();

  // Guards against the close action running twice. The X button fires on
  // pointerdown (see below) so it responds on the very first press even when a
  // browser autofill dropdown or an input blur would otherwise swallow the
  // subsequent click; this ref makes the follow-up click a no-op.
  const closingRef = useRef(false);

  // Close the login page and return to where the user came from.
  // Uses navHistory (React Router's history idx) instead of the unreliable
  // window.history.length, and never pops back onto /login itself — so a single
  // press always leaves this screen instead of appearing to do nothing.
  const handleCloseLogin = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;

    try { localStorage.removeItem('returnUrl'); } catch (e) { /* ignore */ }

    const prev = getPreviousPath();
    if (canGoBack() && prev !== '/login') {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Google Identity Services
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const googleBtnRef = useRef(null);
  const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  // Google's web OAuth flow is refused inside embedded WebViews, so BOTH native
  // platforms use the NativeGoogleAuth plugin instead of the GIS button:
  //   Android — NativeGoogleAuthPlugin.java (Credential Manager)
  //   iOS     — NativeGoogleAuthPlugin.swift (GoogleSignIn SDK)
  // Only the browser/PWA gets the rendered GIS button.
  const isNativeApp = Capacitor.isNativePlatform();

  // Sign in with Apple, per platform:
  //   iOS     — @capacitor-community/apple-sign-in (ASAuthorizationController)
  //   web     — Apple's JS SDK popup (window.AppleID)
  //   Android — no Apple SDK exists, so it uses Apple's web OAuth endpoint in
  //             the system browser and returns through the same custom-scheme
  //             bridge (app.travy.android://apple-callback). See
  //             handleAndroidAppleSignIn below.
  const APPLE_SERVICES_ID = 'app.travy.login';

  // Must be registered as a Return URL on the app.travy.login Services ID.
  // It points at the backend, not the website, because Apple replies with an
  // HTTP POST when the name/email scope is requested — a static page cannot
  // receive that.
  const APPLE_NATIVE_REDIRECT_URI = (
    process.env.REACT_APP_APPLE_NATIVE_REDIRECT_URI ||
    'https://api.travy.buzz/api/auth/apple-native-callback'
  ).trim();

  const APPLE_STATE_KEY = 'apple_oauth_state';
  const APPLE_CONSUMED_KEY = 'apple_oauth_consumed';
  const appleTokenHandledRef = useRef(false);

  // Handle the Google ID-token credential returned by GIS.
  const handleGoogleCredential = async (response) => {
    const credential = response?.credential;
    if (!credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await googleLogin(credential);
      if (result.success) {
        navigate(result.returnUrl || '/dashboard', { replace: true });
      } else {
        setError(result.error || 'Google login failed');
      }
    } catch (err) {
      console.error('[LOGIN] Google login error:', err);
      setError('Google login failed');
    } finally {
      setLoading(false);
    }
  };

  // Keep the latest credential handler in a ref so GIS can be initialized just
  // once (below) while still always calling the current callback — this avoids
  // both the "initialize() called multiple times" console warning and stale
  // state updates from an earlier mount.
  const handleGoogleCredentialRef = useRef(handleGoogleCredential);
  handleGoogleCredentialRef.current = handleGoogleCredential;

  // Initialize & render the Google button (web). In a native Capacitor build,
  // the embedded WebView cannot run Google's web OAuth flow — use a native
  // Google Auth plugin there (see GOOGLE_LOGIN_SETUP.md).
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!GOOGLE_CLIENT_ID) {
      console.warn('[LOGIN] REACT_APP_GOOGLE_CLIENT_ID not set — Google login hidden');
      return;
    }

    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        setTimeout(tryInit, 150);
        return;
      }
      // GIS keeps a single global instance for the page, so initialize() must
      // run only once — re-running it on every mount triggers the
      // "initialize() is called multiple times" warning. The callback delegates
      // to a ref so it always invokes the current component's handler.
      if (!window.__travyGsiInitialized) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp) => handleGoogleCredentialRef.current?.(resp),
        });
        window.__travyGsiInitialized = true;
      }
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'continue_with',
        });
      }
    };
    tryInit();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID]);

  const handleNativeGoogleSignIn = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google Client ID is not configured.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const nativeResult = await NativeGoogleAuth.signIn({
        serverClientId: GOOGLE_CLIENT_ID,
      });
      const credential = nativeResult?.idToken;
      if (!credential) {
        throw new Error('No Google ID token received');
      }

      const result = await googleLogin(credential);
      if (result.success) {
        navigate(result.returnUrl || '/dashboard', { replace: true });
      } else {
        setError(result.error || 'Google login failed');
      }
    } catch (err) {
      // 'cancelled' is the user backing out of Google's sheet — both native
      // plugins spell it the same way on purpose, and it deserves no banner.
      const code = err?.code || err?.error;
      if (code === 'not_configured') {
        console.error('[LOGIN] Native Google login is not configured on this platform:', err);
        setError('Google sign-in is not available in this build.');
      } else if (code !== 'cancelled') {
        console.error('[LOGIN] Native Google login error:', err);
        setError('Google login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clean up any stale or unwanted returnUrl on mount
  // We only allow specific URLs to persist as post-login redirects
  useEffect(() => {
    const ru = localStorage.getItem('returnUrl');
    const allowedReturnUrls = ['/tour-guide', '/review_input', '/coupon_input'];
    const isTourShareLink = /^\/join-tour\/[A-Za-z0-9_-]+$/.test(ru || '');
    const isPanelDeepLink = /^\/(home)?\?panel=(saved|profile)$/.test(ru || '');
    if (ru && !allowedReturnUrls.includes(ru) && !isTourShareLink && !isPanelDeepLink) {
      localStorage.removeItem('returnUrl');
    }
  }, []);

  // Initialize Apple Sign In once when component mounts (web only)
  useEffect(() => {
    // Only initialize for web (not native iOS)
    if (Capacitor.isNativePlatform()) {
      console.log('[LOGIN] Native platform detected, skipping web Apple Sign In init');
      return;
    }

    // Wait for Apple SDK to be fully loaded
    const initializeAppleSignIn = () => {
      if (!window.AppleID) {
        console.log('[LOGIN] Apple SDK not loaded yet, retrying...');
        return false;
      }

      try {
        // Get the correct redirect URI based on environment
        const redirectURI = window.location.hostname === 'localhost'
          ? 'http://localhost:3000/login'
          : 'https://travy.buzz/login';

        console.log('[LOGIN] Initializing Apple Sign In with config:', {
          clientId: 'app.travy.login',
          redirectURI: redirectURI,
          hostname: window.location.hostname,
          scope: 'email name',
          usePopup: true
        });

        // Initialize Apple ID once
        window.AppleID.auth.init({
          clientId: 'app.travy.login',
          scope: 'email name',
          redirectURI: redirectURI,
          state: 'web_apple_login',
          usePopup: true
        });

        // Add event listeners for Apple Sign In events
        document.addEventListener('AppleIDSignInOnSuccess', (event) => {
          console.log('✅ [LOGIN] AppleIDSignInOnSuccess event fired:', event.detail);
        });

        document.addEventListener('AppleIDSignInOnFailure', (event) => {
          console.error('❌ [LOGIN] AppleIDSignInOnFailure event fired:', event.detail);
        });

        setAppleIdInitialized(true);
        console.log('✅ [LOGIN] Apple Sign In initialized successfully');
        return true;
      } catch (err) {
        console.error('❌ [LOGIN] Failed to initialize Apple Sign In:', err);
        return false;
      }
    };

    // Try to initialize immediately
    if (!initializeAppleSignIn()) {
      // If failed, retry after SDK loads
      const checkInterval = setInterval(() => {
        if (initializeAppleSignIn()) {
          clearInterval(checkInterval);
        }
      }, 100);

      // Timeout after 5 seconds
      const timeoutId = setTimeout(() => {
        clearInterval(checkInterval);
        console.error('❌ [LOGIN] Apple SDK failed to load within timeout');
      }, 5000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
      };
    }
    // Run once on mount — re-running on appleIdInitialized would re-init the
    // Apple SDK and stack duplicate document event listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API_URL now handled by the api instance configuration
  // Configure api instance (already configured in axiosConfig.js)

  // REMOVED: handleSuccessfulAuth - AuthContext now handles returnUrl

  // ---------------------------------------------------------------------------
  // Sign in with Apple on Android
  // ---------------------------------------------------------------------------

  // Send the browser to Apple's authorization endpoint. Inside the Capacitor
  // WebView appleid.apple.com is not in allowNavigation, so Capacitor hands this
  // to the system browser — which is required anyway, since Google/Apple both
  // refuse OAuth inside embedded WebViews.
  const handleAndroidAppleSignIn = () => {
    appleTokenHandledRef.current = false;

    let state = 'apple_login';
    try {
      const bytes = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem(APPLE_STATE_KEY, state);
    } catch (e) {
      state = 'apple_login';
    }

    const url = new URL('https://appleid.apple.com/auth/authorize');
    url.searchParams.append('client_id', APPLE_SERVICES_ID);
    url.searchParams.append('redirect_uri', APPLE_NATIVE_REDIRECT_URI);
    url.searchParams.append('response_type', 'code id_token');
    // Apple REQUIRES form_post whenever the name or email scope is requested.
    // Dropping the scope to get a simple query redirect would also drop the
    // email claim from the identity token, leaving accounts with no email to
    // match on — so we keep the scope and let the backend bridge catch the POST.
    url.searchParams.append('response_mode', 'form_post');
    url.searchParams.append('scope', 'name email');
    url.searchParams.append('state', state);

    window.location.href = url.toString();
  };

  // Finish the Android flow with what came back through the deep link.
  const handleAndroidAppleCallback = async (identityToken, authorizationCode, rawUser) => {
    setLoading(true);
    setError('');

    try {
      // Apple sends `user` (the real name) exactly once — on the very first
      // authorization for this Services ID. Every later sign-in omits it, and
      // the backend falls back to the token's email claim.
      let parsedUser = null;
      if (rawUser) {
        try {
          parsedUser = JSON.parse(rawUser);
        } catch (e) {
          console.warn('[LOGIN] Could not parse Apple user payload:', e.message);
        }
      }

      const givenName = parsedUser?.name?.firstName || null;
      const familyName = parsedUser?.name?.lastName || null;

      const loginResult = await appleLogin({
        identityToken,
        authorizationCode: authorizationCode || null,
        user: givenName || familyName ? { firstName: givenName, lastName: familyName } : null,
        email: parsedUser?.email || null,
        givenName,
        familyName
      });

      if (loginResult.success) {
        const redirectTo = loginResult.returnUrl || '/dashboard';
        console.log('✅ [LOGIN] Apple login (Android) successful, redirecting to:', redirectTo);
        navigate(redirectTo, { replace: true });
      } else {
        console.error('❌ [LOGIN] Backend rejected Android Apple login:', loginResult.error);
        setError('การเข้าสู่ระบบด้วย Apple ล้มเหลว กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err) {
      console.error('❌ [LOGIN] Android Apple login error:', err);
      setError('การเข้าสู่ระบบด้วย Apple ล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setLoading(false);
    }
  };

  // Deep-link callback: useDeepLinks turns app.travy.android://apple-callback
  // into /login?apple_id_token=…&apple_state=… (prefixed so Apple's `code` does
  // prefixed to keep Apple's params namespaced on this route).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const identityToken = params.get('apple_id_token');
    const appleError = params.get('apple_error');

    if (appleError) {
      console.warn('[LOGIN] Apple returned an error:', appleError);
      if (appleError !== 'user_cancelled_authorize') {
        setError('การเข้าสู่ระบบด้วย Apple ล้มเหลว กรุณาลองใหม่อีกครั้ง');
      }
      navigate('/login', { replace: true });
      return;
    }

    if (!identityToken) return;

    const expectedState = localStorage.getItem(APPLE_STATE_KEY);
    const returnedState = params.get('apple_state');
    if (expectedState && returnedState !== expectedState) {
      console.warn('[LOGIN] Ignoring Apple callback with unexpected state');
      navigate('/login', { replace: true });
      return;
    }

    if (appleTokenHandledRef.current) return;

    // Replay protection: a cold-start launch URL can be delivered
    // twice, and an identity token is only good for one exchange. Store a short
    // suffix rather than the whole 1KB+ token.
    const tokenKey = identityToken.slice(-32);
    let consumed = [];
    try {
      const raw = JSON.parse(localStorage.getItem(APPLE_CONSUMED_KEY) || '[]');
      consumed = Array.isArray(raw) ? raw : [];
    } catch (e) { /* ignore */ }

    if (consumed.includes(tokenKey)) {
      console.log('[LOGIN] Apple identity token already used — skipping replay');
      navigate(isAuthenticated ? '/dashboard' : '/login', { replace: true });
      return;
    }

    appleTokenHandledRef.current = true;
    try {
      localStorage.setItem(APPLE_CONSUMED_KEY, JSON.stringify([...consumed, tokenKey].slice(-5)));
      localStorage.removeItem(APPLE_STATE_KEY);
    } catch (e) { /* ignore */ }

    const authorizationCode = params.get('apple_code');
    const rawUser = params.get('apple_user');

    // Drop the token from the URL before any await.
    navigate('/login', { replace: true });

    handleAndroidAppleCallback(identityToken, authorizationCode, rawUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegistering) {
        // Registration
        if (!name) {
          setError('ชื่อไม่สามารถว่างเปล่าได้');
          setLoading(false);
          return;
        }

        const result = await registerUser(email, password, name);

        if (result.success) {
          // Registration returns { success: true, returnUrl: '/dashboard' }
          const redirectTo = result.returnUrl || '/dashboard';
          console.log('✅ [LOGIN] Registration successful, redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        } else {
          setError(result.error || 'Registration failed');
        }
      } else {
        // Login
        const result = await login(email, password);

        if (result.success) {
          // Login returns { success: true, returnUrl: '/dashboard' }
          const redirectTo = result.returnUrl || '/dashboard';
          console.log('✅ [LOGIN] Login successful, redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        } else {
          setError(result.error || 'Login failed');
        }
      }
    } catch (err) {
      console.error('[LOGIN] Error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Apple Sign In handler - works for both native iOS and web
  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      // Android: no Apple SDK — hand off to Apple's web flow in the system
      // browser and return through the custom-scheme bridge. Returns straight
      // away; the rest happens in the deep-link effect above.
      if (isNativeAndroid) {
        handleAndroidAppleSignIn();
        return;
      }

      // Check if running on native iOS
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        // Native iOS flow using Capacitor plugin.
        // clientId/redirectURI are only used by the web/Android bridge; on
        // native iOS the plugin authenticates against the app's own bundle ID
        // (app.travy.mobile) via ASAuthorizationController.
        const result = await SignInWithApple.authorize({
          clientId: 'app.travy.mobile', // Your app's bundle ID
          redirectURI: 'https://api.travy.buzz/login', // Your redirect URI
          scopes: 'email name',
          state: 'apple_login'
        });

        console.log('[LOGIN] Native Apple Sign In result:', result);

        // Guard against a malformed response so we never surface a cryptic
        // "Cannot read property 'identityToken' of undefined" to the user.
        const appleResponse = result?.response;
        if (!appleResponse?.identityToken) {
          throw new Error('No identity token received from Apple');
        }

        // Send the authorization data to backend.
        // email/givenName/familyName are only returned by Apple on the FIRST
        // sign-in; on later sign-ins they are null and the backend falls back
        // to the token's email claim.
        const loginResult = await appleLogin({
          identityToken: appleResponse.identityToken,
          authorizationCode: appleResponse.authorizationCode,
          user: appleResponse.user,
          email: appleResponse.email,
          givenName: appleResponse.givenName,
          familyName: appleResponse.familyName
        });

        if (loginResult.success) {
          const redirectTo = loginResult.returnUrl || '/dashboard';
          console.log('✅ [LOGIN] Apple login successful, redirecting to:', redirectTo);
          navigate(redirectTo, { replace: true });
        } else {
          console.error('❌ [LOGIN] Backend rejected native Apple login:', loginResult.error);
          setError('การเข้าสู่ระบบด้วย Apple ล้มเหลว กรุณาลองใหม่อีกครั้ง');
        }
      } else {
        // Web flow using Apple JS SDK
        if (!window.AppleID) {
          console.error('❌ [LOGIN] Apple SDK not available');
          setError('Apple Sign In is not available. Please refresh the page.');
          setLoading(false);
          return;
        }

        // Check if AppleID is initialized
        if (!appleIdInitialized) {
          console.error('❌ [LOGIN] Apple Sign In not initialized yet');
          setError('Apple Sign In is still loading. Please try again in a moment.');
          setLoading(false);
          return;
        }

        console.log('🍎 [LOGIN] Triggering Apple Sign In popup...');

        try {
          // Trigger sign in (already initialized in useEffect)
          const data = await window.AppleID.auth.signIn();
          console.log('✅ [LOGIN] Apple Sign In popup completed successfully');
          console.log('[LOGIN] Apple Sign In data received:', {
            hasAuthorization: !!data?.authorization,
            hasIdToken: !!data?.authorization?.id_token,
            hasCode: !!data?.authorization?.code,
            hasUser: !!data?.user,
            hasEmail: !!data?.user?.email,
            hasName: !!data?.user?.name
          });

          if (!data?.authorization?.id_token) {
            throw new Error('No identity token received from Apple');
          }

          // Send authorization data to backend
          const applePayload = {
            identityToken: data.authorization.id_token,
            authorizationCode: data.authorization.code,
            user: data.user?.name ? {
              firstName: data.user.name.firstName,
              lastName: data.user.name.lastName
            } : null,
            email: data.user?.email,
            givenName: data.user?.name?.firstName,
            familyName: data.user?.name?.lastName
          };

          console.log('[LOGIN] Sending Apple login data to backend...');
          const loginResult = await appleLogin(applePayload);

          if (loginResult.success) {
            const redirectTo = loginResult.returnUrl || '/dashboard';
            console.log('✅ [LOGIN] Apple login successful, redirecting to:', redirectTo);
            navigate(redirectTo, { replace: true });
          } else {
            console.error('❌ [LOGIN] Backend rejected Apple login:', loginResult.error);
            setError('การเข้าสู่ระบบด้วย Apple ล้มเหลว กรุณาลองใหม่อีกครั้ง');
          }
        } catch (signInError) {
          // Handle specific Apple Sign In errors
          console.error('❌ [LOGIN] Apple Sign In popup error:', signInError);
          throw signInError;
        }
      }
    } catch (err) {
      console.error('❌ [LOGIN] Apple Sign In error:', {
        error: err.error,
        message: err.message,
        code: err.code,
        fullError: err
      });

      // Detect user cancellation across web and native so we never show an
      // error dialog when the user simply dismisses the Apple sheet. Apple's
      // reviewers routinely tap "Cancel", and a scary error there reads as a
      // bug (Guideline 2.1). Native cancellation surfaces the raw NSError
      // string, e.g. "...AuthorizationError error 1001." (canceled) or 1000
      // (unknown/dismissed); web surfaces 'popup_closed_by_user'.
      const rawMessage = (err?.message || err?.error || '').toString();
      const isCancellation =
        err?.error === 'popup_closed_by_user' ||
        rawMessage.includes('popup_closed_by_user') ||
        rawMessage.includes('1001') || // ASAuthorizationError.canceled
        rawMessage.includes('1000') || // ASAuthorizationError.unknown (sheet dismissed)
        rawMessage.toLowerCase().includes('cancel');

      if (isCancellation) {
        console.log('ℹ️ [LOGIN] User cancelled Apple Sign In - not showing error');
        // Don't show error for cancellation
      } else {
        // Show a clear, localized message for genuine failures instead of a
        // raw NSError/technical string.
        console.error('❌ [LOGIN] Showing error to user:', rawMessage);
        setError('การเข้าสู่ระบบด้วย Apple ล้มเหลว กรุณาลองใหม่อีกครั้ง');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" style={{ position: 'relative' }}>
        <button
          type="button"
          aria-label="Close"
          // pointerDown fires before the click event, so the button responds on
          // the first press even if an open autofill dropdown / input blur would
          // otherwise swallow the click. preventDefault stops focus-steal;
          // onClick is kept for keyboard (Enter/Space) activation. handleCloseLogin
          // is guarded so it only runs once.
          onPointerDown={(e) => { e.preventDefault(); handleCloseLogin(); }}
          onClick={handleCloseLogin}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(0,0,0,0.06)',
            color: '#374151',
            fontSize: 22,
            lineHeight: 1,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          ×
        </button>
        <div className="logo-container">
          <h1>Login</h1>
        </div>
        
        <h2>{isRegistering ? 'สมัครสมาชิก' : 'เข้าสู่ระบบ'}</h2>
        
        {error && <div className="error-message">{error}</div>}

        {/* No special return-to-tour UI; post-login routing is now centralized */}

        <form onSubmit={handleSubmit}>
          {isRegistering && (
            <div className="form-group">
              <label htmlFor="name">ชื่อผู้ใช้งาน</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="กรุณากรอกชื่อผู้ใช้งาน"
                autoComplete="name"
                required
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">อีเมล</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              autoComplete="email"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">รหัสผ่าน</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete={isRegistering ? 'new-password' : 'current-password'}
              required
            />
          </div>
          
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'กำลังดำเนินการ...' : isRegistering ? 'สมัครสมาชิก' : 'เข้าสู่ระบบ'}
          </button>
        </form>

        <div className="social-login">
          <div className="separator">
            <span>หรือ</span>
          </div>

          {/*
            The button is hidden entirely when no client ID is configured rather
            than rendered in a broken state — App Review taps every login button
            it can see, and a dead one is a rejection under guideline 2.1.
          */}
          {isNativeApp ? (
            GOOGLE_CLIENT_ID ? (
            <button
              type="button"
              onClick={handleNativeGoogleSignIn}
              className="native-google-button"
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.72H.94v2.33A9 9 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.96 10.7A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.16.28-1.7V4.97H.94A9 9 0 0 0 0 9c0 1.45.34 2.82.94 4.03l3.02-2.33z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .94 4.97L3.96 7.3C4.67 5.16 6.66 3.58 9 3.58z" />
              </svg>
              {isRegistering ? 'Sign up with Google' : 'Sign in with Google'}
            </button>
            ) : null
          ) : (
            <div
              ref={googleBtnRef}
              className="google-button-container"
              style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}
            />
          )}

          <button
            onClick={handleAppleSignIn}
            className="apple-button"
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="white"/>
            </svg>
            {isRegistering ? 'Sign up with Apple' : 'Sign in with Apple'}
          </button>
        </div>
        
        <div className="switch-mode">
          {isRegistering ? (
            <p>มีบัญชีอยู่แล้ว? <button onClick={() => setIsRegistering(false)}>เข้าสู่ระบบ</button></p>
          ) : (
            <p>ยังไม่มีบัญชี? <button onClick={() => setIsRegistering(true)}>สมัครสมาชิก</button></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
